import { ErrorBoundary } from "@/app/error-boundaries/ErrorBoundary";
import {
	AdminProtectedRoute,
	AdminRedirect,
	InvestorProtectedRoute,
	InvestorRedirect,
	ProtectedRoute,
	PublicRoute,
} from "@/app/guards";
import { ROUTES } from "@/app/routes";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

const rel = (path: string) => path.replace(/^\//, "");

import AppLayout from "@/components/layout/AppLayout";
import EditorLayout from "@/components/layout/EditorLayout";
import ErrorLayout from "@/components/layout/ErrorLayout";
import {
	ADMIN_LAYOUT,
	DASHBOARD_LAYOUT,
	HUB_LAYOUT,
} from "@/components/layout/layoutConfigs";

const LoginPage = lazy(() => import("@/presentation/pages/auth/LoginPage"));
const ResetPasswordPage = lazy(
	() => import("@/presentation/pages/auth/ResetPasswordPage"),
);
const VerifyEmailPage = lazy(
	() => import("@/presentation/pages/auth/VerifyEmailPage"),
);
const AcceptInvitationPage = lazy(
	() => import("@/presentation/pages/invitations/AcceptInvitationPage"),
);
const InvestmentProposalPage = lazy(
	() => import("@/presentation/pages/proposals/InvestmentProposalPage"),
);

const DashboardPage = lazy(
	() => import("@/presentation/pages/dashboard/DashboardPage"),
);
const BeneficiariesPage = lazy(
	() => import("@/presentation/pages/beneficiaries/BeneficiariesPage"),
);
const BeneficiaryDetailPage = lazy(
	() => import("@/presentation/pages/beneficiaries/BeneficiaryDetailPage"),
);
const ProjectDetailsPage = lazy(
	() => import("@/presentation/pages/projects/ProjectDetailsPage"),
);
const InvestorsPage = lazy(
	() => import("@/presentation/pages/investors/InvestorsPage"),
);
const InvestmentsMainPage = lazy(
	() => import("@/presentation/pages/investments/InvestmentsPage"),
);
const CalculatorPage = lazy(
	() => import("@/presentation/pages/calculator/CalculatorPage"),
);
const ContractsPage = lazy(
	() => import("@/presentation/pages/contracts/ContractsPage"),
);
const OrganizationGeneralPage = lazy(
	() => import("@/presentation/pages/organization/OrganizationGeneralPage"),
);
const OrganizationUsersPage = lazy(
	() => import("@/presentation/pages/organization/OrganizationUsersPage"),
);
const TemplatesListPage = lazy(
	() => import("@/presentation/pages/templates/TemplatesListPage"),
);
const TemplateDetailPage = lazy(
	() => import("@/presentation/pages/templates/TemplateDetailPage"),
);

const SettingsPage = lazy(
	() => import("@/presentation/pages/settings/SettingsPage"),
);
const HelpPage = lazy(() => import("@/presentation/pages/help/HelpPage"));

const AdminDashboardPage = lazy(
	() => import("@/presentation/pages/admin/AdminDashboardPage"),
);
const AdminOrganizationsPage = lazy(
	() => import("@/presentation/pages/admin/AdminOrganizationsPage"),
);
const AdminOrganizationDetailsPage = lazy(
	() => import("@/presentation/pages/admin/AdminOrganizationDetailsPage"),
);
const AdminUsersPage = lazy(
	() => import("@/presentation/pages/admin/AdminUsersPage"),
);
const AdminUserDetailsPage = lazy(
	() => import("@/presentation/pages/admin/AdminUserDetailsPage"),
);

const HubDashboardPage = lazy(
	() => import("@/presentation/pages/hub/HubDashboardPage"),
);
const HubProfilePage = lazy(
	() => import("@/presentation/pages/hub/HubProfilePage"),
);
const HubInvestmentDetailPage = lazy(
	() => import("@/presentation/pages/hub/HubInvestmentDetailPage"),
);
const HubInvestmentActionPage = lazy(
	() => import("@/presentation/pages/hub/HubInvestmentActionPage"),
);
const HubSigningCallbackPage = lazy(
	() => import("@/presentation/pages/hub/HubSigningCallbackPage"),
);
const HubDocumentViewPage = lazy(
	() => import("@/presentation/pages/hub/HubDocumentViewPage"),
);

const PublicLazyRoute = ({ children }: { children: React.ReactNode }) => (
	<PublicRoute>
		<Suspense fallback={<LoadingScreen variant="minimal" />}>
			{children}
		</Suspense>
	</PublicRoute>
);

const router = createBrowserRouter([
	{
		path: "/",
		children: [
			{
				path: rel(ROUTES.AUTH.LOGIN),
				element: (
					<PublicLazyRoute>
						<LoginPage />
					</PublicLazyRoute>
				),
			},
			{
				path: rel(ROUTES.AUTH.RESET_PASSWORD),
				element: (
					<PublicLazyRoute>
						<ResetPasswordPage />
					</PublicLazyRoute>
				),
			},
			{
				path: rel(ROUTES.AUTH.VERIFY_EMAIL),
				element: (
					<PublicLazyRoute>
						<VerifyEmailPage />
					</PublicLazyRoute>
				),
			},
			{
				path: "organizations/:uuid/invitations/:invitationId/accept",
				element: (
					<PublicLazyRoute>
						<AcceptInvitationPage />
					</PublicLazyRoute>
				),
			},
			{
				path: rel(ROUTES.INVITATIONS.ACCEPT_MEMBER),
				element: (
					<PublicLazyRoute>
						<AcceptInvitationPage />
					</PublicLazyRoute>
				),
			},
			{
				path: "proposals/:uuid",
				element: (
					<PublicLazyRoute>
						<InvestmentProposalPage />
					</PublicLazyRoute>
				),
			},

			{
				element: (
					<ProtectedRoute>
						<AdminRedirect>
							<InvestorRedirect>
								<ErrorBoundary variant="default">
									<AppLayout config={DASHBOARD_LAYOUT} />
								</ErrorBoundary>
							</InvestorRedirect>
						</AdminRedirect>
					</ProtectedRoute>
				),
				children: [
					{ index: true, element: <DashboardPage /> },
					{
						path: rel(ROUTES.BENEFICIARIES.LIST),
						element: <BeneficiariesPage />,
					},
					{ path: "beneficiaries/:id", element: <BeneficiaryDetailPage /> },
					{ path: "projects/:id", element: <ProjectDetailsPage /> },
					{
						path: rel(ROUTES.INVESTMENTS.INVESTORS),
						element: <InvestorsPage />,
					},
					{
						path: rel(ROUTES.INVESTMENTS.MAIN),
						element: <InvestmentsMainPage />,
					},
					{
						path: rel(ROUTES.INVESTMENTS.CALCULATOR),
						element: <CalculatorPage />,
					},
					{
						path: rel(ROUTES.INVESTMENTS.CONTRACTS),
						element: <ContractsPage />,
					},
					{
						path: rel(ROUTES.ORGANIZATION.GENERAL),
						element: <OrganizationGeneralPage />,
					},
					{
						path: "organization/general",
						element: <OrganizationGeneralPage />,
					},
					{
						path: rel(ROUTES.ORGANIZATION.USERS),
						element: <OrganizationUsersPage />,
					},
					{
						path: rel(ROUTES.ORGANIZATION.TEMPLATES),
						element: <TemplatesListPage />,
					},

					{ path: rel(ROUTES.SETTINGS), element: <SettingsPage /> },
					{ path: rel(ROUTES.HELP), element: <HelpPage /> },
					{ path: "*", element: <ErrorLayout errorCode="404" /> },
				],
			},

			{
				element: (
					<ProtectedRoute>
						<AdminRedirect>
							<InvestorRedirect>
								<ErrorBoundary variant="default">
									<EditorLayout />
								</ErrorBoundary>
							</InvestorRedirect>
						</AdminRedirect>
					</ProtectedRoute>
				),
				children: [
					{
						path: rel(ROUTES.ORGANIZATION.TEMPLATE_DETAIL(":id")),
						element: <TemplateDetailPage />,
					},
				],
			},

			{
				path: ROUTES.ADMIN.ROOT,
				element: (
					<ProtectedRoute>
						<AdminProtectedRoute>
							<ErrorBoundary variant="admin">
								<AppLayout config={ADMIN_LAYOUT} />
							</ErrorBoundary>
						</AdminProtectedRoute>
					</ProtectedRoute>
				),
				children: [
					{ index: true, element: <AdminDashboardPage /> },
					{ path: "dashboard", element: <AdminDashboardPage /> },
					{ path: "organizations", element: <AdminOrganizationsPage /> },
					{
						path: "organizations/:uuid",
						element: <AdminOrganizationDetailsPage />,
					},
					{ path: "users", element: <AdminUsersPage /> },
					{ path: "users/:uuid", element: <AdminUserDetailsPage /> },
				],
			},

			{
				path: ROUTES.HUB.ROOT,
				element: (
					<ProtectedRoute>
						<InvestorProtectedRoute>
							<ErrorBoundary variant="hub">
								<AppLayout config={HUB_LAYOUT} />
							</ErrorBoundary>
						</InvestorProtectedRoute>
					</ProtectedRoute>
				),
				children: [
					{ index: true, element: <HubDashboardPage /> },
					{ path: "dashboard", element: <HubDashboardPage /> },
					{ path: "profile", element: <HubProfilePage /> },
					{ path: "investments/:uuid", element: <HubInvestmentDetailPage /> },
					{
						path: "investments/:uuid/action",
						element: <HubInvestmentActionPage />,
					},
					{
						path: "investments/:uuid/documents/:docUuid",
						element: <HubDocumentViewPage />,
					},
					{ path: "signing-callback", element: <HubSigningCallbackPage /> },
				],
			},

			{ path: "*", element: <ErrorLayout errorCode="404" /> },
		],
	},
]);

function AppRouter() {
	return <RouterProvider router={router} />;
}

export default AppRouter;
